import { useState, useEffect } from "react";
import {
	Drawer,
	DrawerClose,
	// DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import type { ProgressiveVideo, VimeoResponse } from "@/api/vimeo.types";
import { calculateVideoSizeMB } from "@/utils/videoDownloadService";
import { Download, Video, Headphones } from "lucide-react";
import { DrawerContentWithoutCancelButton } from "./ui/dropdownMobile";
import { cn } from "@/components/utils/utils";

type MediaDownloadType = "video" | "audio";

interface ResolutionOption {
	resolution: string;
	video: ProgressiveVideo;
	sizeMB: number;
}

interface DownloadVideoDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	videoName: string;
	vimeoData?: VimeoResponse | null;
	onConfirmDownload: (
		resolution: string,
		downloadUrl: string,
		sizeMB: number
	) => void;
	onConfirmAudioDownload?: (
		audioStreamUrl: string,
		estimatedSizeMB: number
	) => void;
	videoSource: "vimeo_source" | "akademia_source" | "unknown";
	akademiaVideoUrl?: string;
	estimatedSizeMB?: number;
	audioStreamUrl?: string;
}

export const DownloadVideoDrawer = ({
	open,
	onOpenChange,
	videoName,
	vimeoData,
	onConfirmDownload,
	onConfirmAudioDownload,
	videoSource,
	akademiaVideoUrl,
	estimatedSizeMB = 0,
	audioStreamUrl,
}: DownloadVideoDrawerProps): JSX.Element => {
	const [selectedResolution, setSelectedResolution] = useState<string>("");
	const [selectedUrl, setSelectedUrl] = useState<string>("");
	const [selectedSize, setSelectedSize] = useState<number>(0);
	const [mediaType, setMediaType] = useState<MediaDownloadType>("video");

	// Reset media type when drawer closes
	useEffect(() => {
		if (!open) {
			setMediaType("video");
			setSelectedResolution("");
			setSelectedUrl("");
			setSelectedSize(0);
		}
	}, [open]);

	// Check if audio download is available (Vimeo source with audio stream URL or callback)
	const isAudioAvailable = videoSource === "vimeo_source" && (audioStreamUrl || onConfirmAudioDownload);

	// Get available resolutions from Vimeo
	const resolutionOptions: Array<ResolutionOption> = vimeoData?.play
		?.progressive
		? vimeoData.play.progressive
				.map((video) => ({
					resolution: `${video.height}p`,
					video,
					sizeMB: calculateVideoSizeMB(video),
				}))
				.sort((a, b) => b.video.height - a.video.height)
		: [];

	const handleResolutionSelect = (option: ResolutionOption): void => {
		setSelectedResolution(option.resolution);
		setSelectedUrl(option.video.link);
		setSelectedSize(option.sizeMB);
	};

	const handleConfirm = (): void => {
		if (mediaType === "audio") {
			// Audio download
			if (onConfirmAudioDownload && audioStreamUrl) {
				// Estimate audio size as ~10% of smallest video size
				const smallestVideo = resolutionOptions[resolutionOptions.length - 1];
				const estimatedAudioSize = smallestVideo 
					? Math.round(smallestVideo.sizeMB * 0.1) 
					: 20;
				onConfirmAudioDownload(audioStreamUrl, estimatedAudioSize);
			}
		} else {
			// Video download
			if (videoSource === "akademia_source" && akademiaVideoUrl) {
				onConfirmDownload("original", akademiaVideoUrl, estimatedSizeMB);
			} else if (selectedResolution && selectedUrl && selectedSize) {
				onConfirmDownload(selectedResolution, selectedUrl, selectedSize);
			}
		}
		onOpenChange(false);
	};

	const canDownload = mediaType === "audio" 
		? isAudioAvailable
		: (videoSource === "akademia_source" && akademiaVideoUrl) ||
		  (videoSource === "vimeo_source" && selectedResolution);

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<DrawerContentWithoutCancelButton>
				<DrawerHeader className="text-left p-8">
					<div className="flex justify-between items-start">
						<div className="flex-1">
							<DrawerTitle className="text-lg font-semibold">
								Pobierz materiał offline
							</DrawerTitle>
							<DrawerDescription className="mt-2 text-sm">
								{videoName}
							</DrawerDescription>
						</div>
					</div>
				</DrawerHeader>

				<div className="px-8 pb-6 space-y-4">
					{/* Media type toggle - only show for Vimeo with audio available */}
					{isAudioAvailable && (
						<div>
							<p className="text-sm font-medium mb-3">Wybierz typ materiału:</p>
							<div className="flex gap-2">
								<button
									onClick={() => { setMediaType("video"); }}
									className={cn(
										"flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2",
										mediaType === "video"
											? "border-blue-500 bg-blue-50"
											: "border-gray-200 hover:border-gray-300"
									)}
								>
									<Video className="w-5 h-5" />
									<span className="font-medium">Video</span>
								</button>
								<button
									onClick={() => { setMediaType("audio"); }}
									className={cn(
										"flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2",
										mediaType === "audio"
											? "border-purple-500 bg-purple-50"
											: "border-gray-200 hover:border-gray-300"
									)}
								>
									<Headphones className="w-5 h-5" />
									<span className="font-medium">Audio</span>
								</button>
							</div>
						</div>
					)}

					{/* Video resolution selection */}
					{mediaType === "video" && videoSource === "vimeo_source" && resolutionOptions.length > 0 && (
						<div>
							<p className="text-sm font-medium mb-3">Wybierz rozdzielczość:</p>
							<div className="space-y-2">
								{resolutionOptions.map((option) => (
									<button
										key={option.resolution}
										onClick={() => {
											handleResolutionSelect(option);
										}}
										className={cn(
											"w-full p-3 rounded-lg border-2 transition-all",
											selectedResolution === option.resolution
												? "border-blue-500 bg-blue-50"
												: "border-gray-200 hover:border-gray-300"
										)}
									>
										<div className="flex justify-between items-center">
											<span className="font-medium">{option.resolution}</span>
											<span className="text-sm text-gray-600">
												{option.sizeMB} MB
											</span>
										</div>
									</button>
								))}
							</div>
						</div>
					)}

					{/* Audio info */}
					{mediaType === "audio" && (
						<div className="p-4 bg-purple-50 rounded-lg">
							<div className="flex items-center gap-3">
								<Headphones className="w-8 h-8 text-purple-600" />
								<div>
									<p className="text-sm font-medium text-purple-900">
										Odsłuch audio
									</p>
									<p className="text-xs text-purple-700 mt-1">
										Pobiera tylko ścieżkę audio - mniejszy rozmiar, idealny do słuchania
									</p>
								</div>
							</div>
						</div>
					)}

					{mediaType === "video" && videoSource === "akademia_source" && (
						<div className="p-4 bg-gray-50 rounded-lg">
							<p className="text-sm">
								<span className="font-medium">Rozmiar: </span>~{estimatedSizeMB}{" "}
								MB
							</p>
						</div>
					)}

					{videoSource === "unknown" && (
						<div className="p-4 bg-red-50 rounded-lg">
							<p className="text-sm text-red-600">
								Nie można pobrać tego materiału. Nieznane źródło.
							</p>
						</div>
					)}

					<div className="bg-yellow-50 p-3 rounded-lg">
						<p className="text-xs text-yellow-800">
							<strong>Uwaga:</strong> {mediaType === "audio" ? "Audio" : "Wideo"} zostanie pobrane na pamięć
							urządzenia. Upewnij się, że masz wystarczająco dużo miejsca.
						</p>
					</div>

					<div className="flex justify-between gap-2 pt-2">
						<DrawerClose asChild>
							<Button variant="link" className="underline text-gray-400">
								Nie pobieraj
							</Button>
						</DrawerClose>
						<Button
							onClick={handleConfirm}
							disabled={!canDownload}
							className="border-[1px] border-[#919EAB3D] px-2 py-1"
							variant="ghost"
						>
							<Download className="mr-2 h-4 w-4" />
							{mediaType === "audio" ? "Pobierz Audio" : "Pobierz Video"}
						</Button>
					</div>
				</div>
			</DrawerContentWithoutCancelButton>
		</Drawer>
	);
};
